import React from 'react'
import styled from '@emotion/styled'
import {Box} from '@vio/atlas/helpers/Box'
import {Flex} from '@vio/atlas/helpers/Flex'
import {NoStyleLink} from '@vio/atlas/helpers/NoStyleLink'
import {SkeletonLoader} from '@vio/atlas/SkeletonLoader'
import {StarRating} from '@vio/atlas/StarRating'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import useGenerateAppDownloadUrl from 'hooks/useGenerateAppDownloadUrl'
import {useGetAppRatingsQuery} from 'modules/appRatings'

import {Placement} from '@daedalus/core/src/deepLinking/types'
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage'

const googlePlay = `/static/hero-section/hero-section-google-play.svg`
const appStore = `/static/hero-section/hero-section-apple-store.svg`

const StyledStarRating = styled(StarRating)`
  & svg {
    fill: ${cssTheme.colors.content.warning.c500};
  }
`

interface Props {
  variant: 'GooglePlay' | 'AppStore'
}
export const AppRatingsAndReviews: React.FC<Props> = ({variant}) => {
  const {data, isLoading} = useGetAppRatingsQuery()
  const {generateAppStoreDeepLink, generatePlayStoreDeepLink} =
    useGenerateAppDownloadUrl()

  if (isLoading)
    return (
      <Box display="flex" gap="s300">
        <SkeletonLoader width={40} height={40} />
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          gap="s100"
        >
          <SkeletonLoader width={70} />
          <SkeletonLoader width={100} />
        </Box>
      </Box>
    )

  const appDownloadUrl =
    variant === 'AppStore'
      ? generateAppStoreDeepLink(Placement.HomePageHeroSection)
      : generatePlayStoreDeepLink(Placement.HomePageHeroSection)

  if (variant === 'AppStore') {
    const {ios = {ratingScore: 5, ratingCount: 5000}} = data || {}
    return (
      <NoStyleLink
        href={appDownloadUrl}
        data-id="AppleAppStoreBadge"
        target="_blank"
      >
        <Flex.Row alignItems="center" gap="s300">
          <img src={appStore} alt="app-store" />
          <Flex.Column gap="s100" alignItems="flex-start">
            <StyledStarRating size="xs" rating={Math.ceil(ios.ratingScore)} />
            <Text variant="bodyXS">
              <FormattedMessageWrapper
                id="appRatingReviews"
                defaultMessage="{rating} · {reviews, plural, one {1 review} other {# reviews}}"
                values={{
                  rating: ios.ratingScore.toFixed(1),
                  reviews: ios.ratingCount
                }}
              />
            </Text>
          </Flex.Column>
        </Flex.Row>
      </NoStyleLink>
    )
  }

  const {android = {ratingScore: 5, ratingCount: 5000}} = data || {}
  return (
    <NoStyleLink
      href={appDownloadUrl}
      data-id="GooglePlayBadge"
      target="_blank"
    >
      <Flex.Row alignItems="center" gap="s300">
        <img src={googlePlay} alt="google-play" />
        <Flex.Column>
          <StyledStarRating size="xs" rating={Math.ceil(android.ratingScore)} />
          <Text variant="bodyXS">
            <FormattedMessageWrapper
              id="appRatingReviews"
              defaultMessage="{rating} · {reviews, plural, one {1 review} other {# reviews}}"
              values={{
                rating: android.ratingScore.toFixed(1),
                reviews: android.ratingCount
              }}
            />
          </Text>
        </Flex.Column>
      </Flex.Row>
    </NoStyleLink>
  )
}
